import { useState } from "react";
import { styled, VStack, Wrap } from "../../../styled-system/jsx";
import { GridListItem, GridList } from "react-aria-components";
import { grid } from "../../../styled-system/patterns";
import { Button } from "../core/basic/Button";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Loading from "../core/blocks/Loading";
import { apiClient } from "../../zodios/client";
import { HiLightBulb } from "react-icons/hi";
import { css } from "../../../styled-system/css";
import type { RecommendationItem } from "../../zodios/apiDefinitions/merchandise";

type KeywordSelectorProps = {
  productIds: string[];
  marketplace: string;
  filledTags: string[];
  onChangeFilledTags: (next: string[]) => void;
};

const normalize = (item: RecommendationItem) => {
  return item.keyword;
};

const KeywordCard = styled(GridListItem, {
  base: {
    all: "unset",
    cursor: "pointer",
    display: "flex",
    position: "relative",
    gap: "small",
    flexDirection: "row",
    alignItems: "center",
    border: "1px solid {colors.lightgrey}",
    borderRadius: "standard",
    padding: "{radii.standard}",
    "&[data-focus-visible='true']": { outline: "2px solid {colors.trellisPalette.3}" },
  },
});

const mergeUniqueTags = (existing: string[], add: string[]) => {
  const map = new Map<string, string>();
  for (const t of existing) map.set(t.trim().toLowerCase(), t.trim());
  for (const t of add) {
    const k = t.trim().toLowerCase();
    if (k && !map.has(k)) map.set(k, t.trim());
  }
  return Array.from(map.values());
};

export const KeywordSelector = ({ productIds, marketplace, filledTags, onChangeFilledTags }: KeywordSelectorProps) => {
  const { t } = useTranslation(["advertising"]);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["kw-recos", marketplace, productIds],
    queryFn: async () => {
      const res = await apiClient.getKeywordRecommendations({
        queries: {
          marketplace,
          maxNumSuggestions: 100,
          productIds,
        },
      });
      return res.recommendations.map(normalize).filter(Boolean);
    },
    enabled: open && productIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const filledLower = new Set(filledTags.map((t) => t.toLowerCase()));
  const allKeywords = data || [];
  const keywords = allKeywords.filter((k) => !filledLower.has(k.toLowerCase()));
  const suggestionLower = new Set(allKeywords.map((k) => k.toLowerCase()));
  const hasSuggestedInFilled = filledTags.some((t) => suggestionLower.has(t.toLowerCase()));

  const handlePick = (kw: string) => {
    onChangeFilledTags(mergeUniqueTags(filledTags, [kw]));
  };

  const handleSelectAll = () => {
    if (!keywords.length) return;
    onChangeFilledTags(mergeUniqueTags(filledTags, keywords));
  };

  const handleClearAllSuggested = () => {
    if (!suggestionLower.size) return;
    const remaining = filledTags.filter((t) => !suggestionLower.has(t.toLowerCase()));
    onChangeFilledTags(remaining);
  };

  return (
    <VStack gap="medium" alignItems="stretch">
      <Wrap gap="small" justify="flex-start">
        <Button variant="link" onPress={() => setOpen((v) => !v)} paddingTop="small">
          <span>
            {open ?
              t(($) => $.advertising["Hide Amazon Suggested Keywords"])
            : t(($) => $.advertising["Amazon Suggested Keywords"])}
          </span>{" "}
          <HiLightBulb />
        </Button>
      </Wrap>

      {!open ? null : (
        <div
          className={css({
            border: "keywordSelector",
            borderRadius: "small",
            padding: "medium",
          })}
        >
          <Wrap gap="small" marginBottom="small">
            <Button variant="link" onPress={handleSelectAll} isDisabled={!keywords.length}>
              {t(($) => $.advertising["Select All"])}
            </Button>
            <Button variant="link" onPress={handleClearAllSuggested} isDisabled={!hasSuggestedInFilled}>
              {t(($) => $.advertising["Clear All"])}
            </Button>
          </Wrap>

          {isLoading && <Loading fullPage={false} />}

          {!isLoading &&
            (keywords.length === 0 ?
              <div>{t(($) => $.advertising["No suggestions"])}</div>
            : <GridList
                aria-label={t(($) => $.advertising["Keyword selector"])}
                className={grid({
                  minChildWidth: "200px",
                  maxHeight: "60vh",
                  padding: "2px",
                  scrollbarColor: "{colors.lightgrey} transparent",
                  scrollbarWidth: "thin",
                  overflowY: "auto",
                })}
                selectionMode="single"
                layout="grid"
                items={keywords.map((k) => ({ id: k, label: k }))}
                onSelectionChange={(sel) => {
                  if (sel === "all") return;
                  const picked = Array.from(sel).map(String)[0];
                  if (picked) handlePick(picked);
                }}
              >
                {(item) => (
                  <KeywordCard key={item.id} textValue={item.label}>
                    {item.label}
                  </KeywordCard>
                )}
              </GridList>)}
        </div>
      )}
    </VStack>
  );
};
